import { watch } from 'vue'
import { ConnectionState } from 'livekit-client'
import { liveKitManager } from './manager'
import { debugPublisher } from '~/lib/debug/debugPublisher'

/**
 * LiveKit participant attribute key other clients can read off `participant.attributes` to know
 * "has this person deafened themselves (turned off audio output)" — mirrors
 * `SPEAKING_ATTRIBUTE_KEY` in `speakingAttributePublisher.ts` (same broadcast mechanics via
 * `LocalParticipant.setAttributes()` and the same `canUpdateOwnMetadata` grant requirement).
 */
export const DEAFENED_ATTRIBUTE_KEY = 'deafened'

class DeafenedAttributePublisher {
  private started = false
  private sentValue: boolean | null = null

  start() {
    if (this.started) return
    this.started = true

    // A new room means a brand-new server-side participant object — whatever we last sent to
    // the previous one doesn't carry over, so drop the cache. Don't resend here though: at this
    // point `room` was just constructed and `room.connect()` hasn't resolved yet, so
    // `setAttributes()` would race the signal connection and silently fail.
    watch(() => liveKitManager.state.room, () => { this.sentValue = null })
    // `roomState` only flips to `Connected` after `room.connect()` actually resolves (see
    // manager.ts), so this is the reliable point to (re)send — including when `isDeafened` was
    // already `true` before the switch, when the value-watch below won't fire again.
    watch(() => liveKitManager.state.roomState, (state) => {
      if (state === ConnectionState.Connected) this.send(liveKitManager.state.isDeafened)
    })
    watch(() => liveKitManager.state.isDeafened, (value) => this.send(value), { immediate: true })
  }

  private send(value: boolean) {
    const participant = liveKitManager.room?.localParticipant
    if (!participant) {
      debugPublisher.log('livekit', 'warn', `setAttributes(deafened=${value}) пропущен — нет активного localParticipant`)
      return
    }
    if (this.sentValue === value) return
    this.sentValue = value
    debugPublisher.log('livekit', 'info', `-> setAttributes(deafened=${value})`)
    participant.setAttributes({ [DEAFENED_ATTRIBUTE_KEY]: String(value) }).then(() => {
      debugPublisher.log('livekit', 'info', `<- setAttributes(deafened=${value}) подтверждён сервером`)
    }).catch(error => {
      // Same failure mode as speakingAttributePublisher: missing `canUpdateOwnMetadata` grant on
      // the LiveKit access token rejects (often after ~5s) rather than failing fast.
      console.warn('[deafenedAttributePublisher] Failed to update deafened attribute', error)
      debugPublisher.log('livekit', 'error', `setAttributes(deafened=${value}) отклонён: ${error?.message ?? error}`)
    })
  }
}

export const deafenedAttributePublisher = new DeafenedAttributePublisher()
